import { getQueryIntent, type QueryIntentArgs } from '@atlassian/search-ai/getQueryIntent';

/**
 * Arguments accepted by {@link preloadIntentDetection}. Identical to
 * {@link QueryIntentArgs} minus the setter callbacks.
 */
export type PreloadIntentDetectionArgs = Omit<
	QueryIntentArgs,
	| 'setQueryIntent'
	| 'setTransformedQuery'
	| 'setIntentData'
	| 'setRequestId'
	| 'setTraceId'
>;

/*
	* Not required: all callbacks happen after the fetch.
	*/
const noopCallbacks = {
	setIntentData: () => {},
	setQueryIntent: () => {},
	setTransformedQuery: () => {},
	setRequestId: () => {},
	setTraceId: () => {},
};

/**
 * Fire-and-forget early start of `KnowledgeDiscoveryIntentDetectionQuery`.
 *
 * Wraps {@link getQueryIntent} with no-op setters so the call goes through the
 * exact same code path (and the exact same `InMemCache` key) the consumer will
 * later use. When the consumer's `useQueryIntent` eventually calls
 * `getQueryIntent` with the same `query`, the cache layer transparently either
 *  - returns the already-resolved cached response, or
 *  - returns the in-flight promise so the consumer's `await` joins this one,
 *  - calls the necessary callbacks (set by the normal code path).
 *
 * Lives in `search-page` (not `search-ai`) because it is a search-page-only
 * consumption pattern of `getQueryIntent` — the colocated
 * `buildIntentDetectionVariables` is what owns the cache-key shape.
 */
export const preloadIntentDetection = (
	args: PreloadIntentDetectionArgs
): void => {

	getQueryIntent({
		...args,
		...noopCallbacks,
	})
};
